import React, { ChangeEvent } from 'react';
import { IAuthState, IRootState } from '../../../../@types/redux';
import { connect } from 'react-redux';
import firebase from 'firebase/app';
import { createSelector } from 'reselect';
import Typography from '@material-ui/core/Typography';
import Toolbar from '@material-ui/core/Toolbar';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import Icon from '@material-ui/core/Icon';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import LinearProgress from '@material-ui/core/LinearProgress';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import GeneralFunctions from '../../../../store/general/functions';
import PumpingStationMotorMountingHelper, { IPumpingStationMotorMounting } from '../../../../@types/model/masterData/pumpingStation/motorMounting';
import MasterDataPumpingStationMotorMountingFunctions from '../../../../store/masterData/pumpingStation/motorMounting/functions';

interface IPumpingStationMotorMountingListProps {
    auth : IAuthState;

    isLoading : boolean;
    motorMountings : Array<IPumpingStationMotorMounting>;
}

interface IPumpingStationMotorMountingListState {
    searchText : string;

    selected : IPumpingStationMotorMounting | null;

}

class PumpingStationMotorMountingListComponent extends React.PureComponent<IPumpingStationMotorMountingListProps, IPumpingStationMotorMountingListState> {
    constructor(props : IPumpingStationMotorMountingListProps) {
        super(props);
        this.state = {
            searchText: '',
            selected: null,
        };
    }

    public readonly componentDidMount = () => {
        this.load();
    };

    private readonly load = () => {
        MasterDataPumpingStationMotorMountingFunctions.load();
    };

    private readonly onTypeClick = (event : React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        this.setState({
            selected: this.props.motorMountings.find(n => n.id === event.currentTarget.id) ?? null,
        });
    };

    private readonly onSearchChanged = (event : ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        this.setState({
            searchText: event.currentTarget.value,
        });
    };

    private readonly onNameChange = (event : ChangeEvent<HTMLInputElement>) => {
        const { selected } = this.state;

        if (!selected) return;

        this.setState({
            selected: {
                ...selected,
                name: event.currentTarget.value,
            },
            
        });
    };

    private readonly onClearClick = () => {
        this.setState({
            searchText: '',
        });
    };

    private readonly onAddClick = () => {
        if (!this.state.searchText) return;

        if (this.props.motorMountings.some(n => n.name.toLocaleLowerCase() === this.state.searchText.toLocaleLowerCase())) {
            GeneralFunctions.generalShowErrorSnackbar(`${this.state.searchText} already added.`);
            return;
        }

        const pump : IPumpingStationMotorMounting = {
            id: '',
            name: this.state.searchText,
            createdBy: this.props.auth.session?.firebaseUser.uid ?? '',
            createdByEmployee: this.props.auth.session?.employee.EmployeeNumber ?? '',
            createdByName: this.props.auth.session?.employee.Name ?? '',
            createdOn: firebase.firestore.Timestamp.now().toMillis(),
            updatedBy: this.props.auth.session?.firebaseUser.uid ?? '',
            updatedByEmployee: this.props.auth.session?.employee.EmployeeNumber ?? '',
            updatedByName: this.props.auth.session?.employee.Name ?? '',
            updatedOn: firebase.firestore.Timestamp.now().toMillis(),
            isActive: true,
        };

        PumpingStationMotorMountingHelper.save(pump);

        this.setState({
            searchText: '',            
        });
    };

    private readonly onSaveClick = async (event : React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();

        const { selected } = this.state;

        if (!selected) return;
        if (!selected.name) return;

        if (this.props.motorMountings.some(n => n.id !== selected.id && n.name.toLocaleLowerCase() === selected.name.toLocaleLowerCase())) {
            GeneralFunctions.generalShowErrorSnackbar(`${this.state.searchText} already added.`);
            return;
        }

        PumpingStationMotorMountingHelper.save({
            ...selected,
        });
    };

    private readonly onDeleteClick = (event : React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();

        const selected = this.props.motorMountings.find(x => x.id === event.currentTarget.value);
        if (!selected) return;

        PumpingStationMotorMountingHelper.save({
            ...selected,
            isActive: !selected.isActive,
        });
    };

    private readonly getMotorMountings = (state : IPumpingStationMotorMountingListState, props : IPumpingStationMotorMountingListProps) => props.motorMountings;
    private readonly getSearchText = (state : IPumpingStationMotorMountingListState) => state.searchText;

    private readonly getFilteredMountings = createSelector(
        [this.getMotorMountings, this.getSearchText],
        (motorMountings, searchText) => {
            return motorMountings
                .filter(n => n.name.toString().toLocaleLowerCase().includes(searchText.toLocaleLowerCase()))
                .sort((a, b) => a.name.localeCompare(b.name));
        },
    );

    private readonly getTotal = createSelector(
        [this.getMotorMountings],
        (motorMountings) => {
            return motorMountings.length;
        },
    );

    public readonly render = () => {
        const { searchText, selected } = this.state;
        const { isLoading } = this.props;

        const motorMountings = this.getFilteredMountings(this.state, this.props);
        const total = this.getTotal(this.state, this.props);

        return (
            <div className={'fdc ais flx1 p10'}>
                <div className='fdr flx1 hfill'>
                    <div className='fdc flx1 hfill'>
                        <Paper className='fdc flx1 hfill m10'>
                            <Toolbar className={'fdr aic'}>
                                <TextField
                                    value={searchText}
                                    onChange={this.onSearchChanged}
                                    placeholder='Search'
                                    margin='dense'
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <Icon>search</Icon>
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <div>
                                                {
                                                    searchText &&
                                                    <Tooltip title='Clear'>
                                                        <IconButton size='small' onClick={this.onClearClick}>
                                                            <Icon className='cr'>close</Icon>
                                                        </IconButton>
                                                    </Tooltip>
                                                }
                                            </div>
                                        ),
                                    }}
                                />
                                <Tooltip title='Add'>
                                    <div>
                                        <IconButton color='primary' onClick={this.onAddClick} aria-label='Add'>
                                            <Icon>add</Icon>
                                        </IconButton>
                                    </div>
                                </Tooltip>
                            </Toolbar>
                            <Divider />
                            <Typography variant={'subtitle2'} className={'fdr pl20 pr30 pb5 pt5'}>
                                Total {total}
                            </Typography>
                            <Divider />
                            <List className={'fdc flx1 oya'}>
                                <div className='mnh4'>
                                    { isLoading && <LinearProgress />}
                                </div>
                                {
                                    motorMountings.map(n => (
                                        <ListItem button key={n.id} id={n.id} onClick={this.onTypeClick} selected={selected?.id === n.id} >
                                            <ListItemText
                                                className={n.isActive ? '' : 'cr'}
                                                primary={`${n.name}`}
                                            />
                                            {
                                                (n.isActive) &&
                                                <ListItemSecondaryAction>
                                                    <Tooltip title='Disable'>
                                                        <IconButton className='cr' value={n.id} onClick={this.onDeleteClick} aria-label='Delete'>
                                                            <Icon>delete</Icon>
                                                        </IconButton>
                                                    </Tooltip>
                                                </ListItemSecondaryAction>
                                            }
                                            {
                                                (!n.isActive) &&
                                                <ListItemSecondaryAction>
                                                    <Tooltip title='Enable'>
                                                        <IconButton color='primary' value={n.id} onClick={this.onDeleteClick} aria-label='Delete'>
                                                            <Icon>check</Icon>
                                                        </IconButton>
                                                    </Tooltip>
                                                </ListItemSecondaryAction>
                                            }
                                        </ListItem>
                                    ))
                                }
                            </List>
                        </Paper>
                    </div>
                    <div className='fdc flx4 hfill'>
                        {
                            !!selected &&
                            <Paper className='fdc flx1 hfill m10'>
                                <Toolbar className={'fdr aic'}>
                                    <Typography variant='h5' color='inherit'>
                                    Motor Mounting - {selected.name}
                                    </Typography>
                                    <span className='flx1' />
                                    <Tooltip title='Save'>
                                        <div>
                                            <IconButton color='primary' onClick={this.onSaveClick} aria-label='Save'>
                                                <Icon>save</Icon>
                                            </IconButton>
                                        </div>
                                    </Tooltip>
                                </Toolbar>
                                <Divider />
                                <div className='fdc flx1 hfill'>
                                    <div className='fdc flx1 oya'>
                                        <div className='aic p5 mb10 pr20'>
                                            <FormControl fullWidth>
                                                <TextField
                                                    autoComplete='off'
                                                    id='name'
                                                    label='Name'
                                                    value={selected.name ? selected.name : ''}
                                                    required
                                                    error={!selected.name}
                                                    onChange={this.onNameChange}
                                                    margin='normal'
                                                    className={'TextField'}
                                                />
                                                {
                                                    !selected.name &&
                                                    <FormHelperText error>Required</FormHelperText>
                                                }
                                            </FormControl>
                                        </div>
                                    </div>
                                </div>
                            </Paper>
                        }
                    </div>
                </div>
            </div>
        );
    };
}

const mapStateToProps = (state : IRootState) => {
    return {
        auth: state.auth,
        isLoading: state.masterData.pumpingStation.motorMounting.isLoading,
        motorMountings: state.masterData.pumpingStation.motorMounting.motorMountings,
    };
};

const PumpingStationMotorMountingList = connect(
    mapStateToProps,
)(PumpingStationMotorMountingListComponent);

export default PumpingStationMotorMountingList;
